import type { Rebate, Withdrawal, Transaction } from '../../types';

interface RecentTransactionsProps {
  rebates: Rebate[];
  withdrawals: Withdrawal[];
  transactions?: Transaction[];
  formatCurrency: (amount: number) => string;
  onViewAll: () => void;
  viewAllLabel: string;
  noTransactionsText: string;
}

const RecentTransactions = ({
  rebates,
  withdrawals,
  transactions = [],
  formatCurrency,
  onViewAll,
  viewAllLabel,
  noTransactionsText,
}: RecentTransactionsProps) => {
  // Combine all transactions and sort by createdAt (newest first)
  const allTransactions = [
    ...rebates.map(r => ({ ...r, type: 'rebate' as const })),
    ...withdrawals.map(w => ({ ...w, type: 'withdrawal' as const })),
    ...transactions.map(t => ({ ...t, type: 'transaction' as const, transactionType: (t as any).transactionType || t.type })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-white">Recent Transactions</h3>
        <button 
          className="text-primary text-sm flex items-center gap-1 hover:text-primary/80 hover:scale-105 transition-all duration-300"
          onClick={onViewAll}
        >
          {viewAllLabel} →
        </button>
      </div>
      {allTransactions.length > 0 ? (
        <div className="flex flex-col gap-2">
          {allTransactions.map((tx) => (
            <div key={tx.id} className="bg-tg-secondary rounded-lg p-3 flex justify-between items-center border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 cursor-pointer">
              <div className="flex-1">
                <div className="text-sm text-white mb-1">
                  {tx.type === 'transaction' 
                    ? ('transactionType' in tx && tx.transactionType === 'bonus' ? 'BONUS' : ('transactionType' in tx ? tx.transactionType.toUpperCase() : 'TRANSACTION'))
                    : 'exchange' in tx 
                    ? tx.exchange.toUpperCase() 
                    : tx.network}
                </div>
                <div className="text-xs text-tg-hint">
                  {tx.type === 'transaction' 
                    ? tx.description 
                    : 'description' in tx 
                    ? tx.description 
                    : `${tx.network} withdrawal`}
                </div>
              </div>
              <div className={`text-base font-semibold ${
                (tx.type === 'rebate' || (tx.type === 'transaction' && 'transactionType' in tx && (tx.transactionType === 'bonus' || tx.transactionType === 'rebate'))) && tx.status === 'completed' ? 'text-primary' : 
                tx.type === 'withdrawal' && tx.status === 'completed' ? 'text-primary' : 
                'text-orange-500'
              }`}>
                {tx.status === 'completed' 
                  ? (tx.type === 'withdrawal' 
                    ? '-' 
                    : (tx.type === 'rebate' || (tx.type === 'transaction' && 'transactionType' in tx && (tx.transactionType === 'bonus' || tx.transactionType === 'rebate'))) 
                    ? '+' 
                    : '')
                  : ''}{formatCurrency(tx.amount)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-tg-secondary rounded-xl p-8 text-center">
          <div className="text-4xl mb-2">📄</div>
          <div className="text-white mb-1">{noTransactionsText}</div>
          <div className="text-sm text-tg-hint">Start trading to see your transactions</div>
        </div>
      )}
    </div>
  );
};

export default RecentTransactions;

